import {
  TimePrescreeningRescreenRequestDtoType,
  TimePrescreeningScreeningDecisionRequestDtoType,
} from '@tempus/trials-types';

import { RescreenEligibilityReviewRowReq, UpdateEligibilityReviewRowReq } from '../types/timeScreeningApi';
import { PatientCohortMatchWireRow, UnscreenedMatchRowDto } from '../types/timePrescreeningApi';

export function getSnoozeDisplayDate(
  snoozeData: UnscreenedMatchRowDto['userInput']['snoozeData'],
): string | Date | undefined {
  if (!snoozeData) {
    return undefined;
  }

  const items = Array.isArray(snoozeData) ? snoozeData : [snoozeData];
  const first = items[0] as { date?: string | Date; data?: { date?: string | Date } };

  return first?.date ?? first?.data?.date;
}

/** Normalizes snooze payload to a `YYYY-MM-DD` or display string for form fields. */
export function formatSnoozeDisplayDateForInput(snoozeData: UnscreenedMatchRowDto['userInput']['snoozeData']): string {
  const snooze = getSnoozeDisplayDate(snoozeData);
  if (typeof snooze === 'string') {
    return snooze;
  }
  if (snooze instanceof Date) {
    return snooze.toISOString().slice(0, 10);
  }
  return '';
}

type EvalEngineRuleWire = {
  id: string;
  name?: string;
  result?: boolean;
  matches?: boolean;
  type?: string;
};

function isSupportingEvalEngineRule(rule: EvalEngineRuleWire): boolean {
  return rule.type === 'supporting';
}

type RuleNameSnapshotWire = {
  name?: string;
  displayText?: string;
};

function getEvalEngineRules(
  evaluationEngineDeterminedResult: UnscreenedMatchRowDto['evaluationEngineDeterminedResult'],
): EvalEngineRuleWire[] {
  if (!evaluationEngineDeterminedResult || typeof evaluationEngineDeterminedResult !== 'object') {
    return [];
  }

  const { rules } = evaluationEngineDeterminedResult as { rules?: unknown };

  return Array.isArray(rules) ? (rules as EvalEngineRuleWire[]) : [];
}

function isFailedEvalEngineRule(rule: EvalEngineRuleWire): boolean {
  if (typeof rule.result === 'boolean') {
    return !rule.result;
  }

  if (typeof rule.matches === 'boolean') {
    return !rule.matches;
  }

  return false;
}

export function getRuleDisplayName(rule: EvalEngineRuleWire, snapshot?: RuleNameSnapshotWire): string {
  const name = snapshot?.displayText?.trim() || snapshot?.name?.trim() || rule.name?.trim() || rule.id;

  return name.replace(/_/g, ' ');
}

export function getFailedRuleNamesFromArmEvaluationRow(
  row: Pick<UnscreenedMatchRowDto, 'evaluationEngineDeterminedResult' | 'rulesetSnapshot'>,
): string[] {
  const rules = getEvalEngineRules(row.evaluationEngineDeterminedResult);

  if (rules.length === 0) {
    return [];
  }

  const rulesById = new Map(rules.map((rule) => [rule.id, rule]));
  const scopedRules: { rule: EvalEngineRuleWire; snapshot?: RuleNameSnapshotWire }[] =
    row.rulesetSnapshot.length > 0
      ? row.rulesetSnapshot.flatMap((snapshot) => {
          const rule = rulesById.get(snapshot.id);
          return rule ? [{ rule, snapshot }] : [];
        })
      : rules.map((rule) => ({ rule }));

  return scopedRules
    .filter(({ rule }) => isFailedEvalEngineRule(rule) && !isSupportingEvalEngineRule(rule))
    .map(({ rule, snapshot }) => getRuleDisplayName(rule, snapshot));
}

export function buildScreeningDecisionRequest(body: UpdateEligibilityReviewRowReq): {
  evaluationId: string;
  payload: TimePrescreeningScreeningDecisionRequestDtoType;
} {
  const { evaluationId, decisionEvent, userInput } = body;

  return {
    evaluationId,
    payload: {
      decisionEvent: decisionEvent as TimePrescreeningScreeningDecisionRequestDtoType['decisionEvent'],
      ...(userInput && Object.keys(userInput).length > 0 && { userInput }),
    },
  };
}

export function buildRescreenRequest(body: RescreenEligibilityReviewRowReq): {
  evaluationId: string;
  payload: TimePrescreeningRescreenRequestDtoType;
} {
  const { evaluationId, decisionEvent, userInput } = body;

  return {
    evaluationId,
    payload: {
      decisionEvent: decisionEvent as TimePrescreeningRescreenRequestDtoType['decisionEvent'],
      ...(userInput && { updatedData: userInput }),
    },
  };
}

export function unwrapRescreenEvaluationRow(data: Record<string, unknown>): UnscreenedMatchRowDto {
  const row = data.timePatientArmEvaluation ?? data;

  return row as UnscreenedMatchRowDto;
}

export function getInternalRnToScreeningRnNote(
  patientCohortMatches: PatientCohortMatchWireRow[] | undefined,
  armId: string,
): string | null {
  return (
    patientCohortMatches
      ?.filter((match) => match.armId === armId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]?.additionalDetails
      ?.internalRnToScreeningRnNote ?? null
  );
}
